import useLocale from 'next-translate/useTranslation';

import { HAS_FREE_PLAN } from '@/constants/common';
import type { InputField, Preset } from '@/constants/presets';

/**
 * The page's `t` (the `tools` namespace). A key a locale hasn't translated
 * yet falls back to the English key with its {{placeholders}} filled in;
 * next-translate otherwise returns a missing key raw, placeholders and all.
 */
export function useTemplateTranslation() {
  const { t, lang } = useLocale('tools');
  return {
    lang,
    t: (key: string, query?: Record<string, unknown>) =>
      t(key, query, { default: key }),
  };
}

/** Sign-up link that opens this template right after registration. */
export function templateSignupHref(
  preset: Pick<Preset, 'slug' | 'loginPreset'>,
) {
  if (!HAS_FREE_PLAN) return '/#pricing';
  return `/login?preset=${encodeURIComponent(
    preset.loginPreset || preset.slug,
  )}#auth-sign-up`;
}

/** A curated example output, as getStaticProps passes it. */
export type TemplateExample = {
  id: number;
  completion: string | null;
  request_body?: { keywords?: string; [key: string]: any } | null;
  preset: string | null;
  user_prompt: string | null;
};

const isChoice = (field?: InputField) =>
  field?.type === 'select' || field?.type === 'radio';

/** What the example was made from, for its "Input" line. */
export function exampleInput(
  example: TemplateExample,
  fields: readonly InputField[] = [],
): string {
  // A choice field's value shows with the options instead.
  if (isChoice(fields.find((field) => field.name === 'keywords'))) return '';
  const keywords = example.request_body?.keywords;
  const text =
    typeof keywords === 'string' && keywords.trim()
      ? keywords
      : example.user_prompt || '';
  return text.trim();
}

/**
 * The choices the example used ("Female", "Funny"), shown beside its input.
 * Only values the field still offers: older examples can carry options that
 * have since been renamed.
 */
export function exampleOptions(
  example: TemplateExample,
  fields: readonly InputField[] = [],
): string[] {
  const body = example.request_body || {};
  return fields
    .filter(isChoice)
    .map((field) => {
      const value = body[field.name];
      return typeof value === 'string' && fieldOptions(field).includes(value)
        ? value
        : null;
    })
    .filter((value): value is string => !!value);
}

/** A related template, slimmed down for the page's props. */
export type RelatedTemplate = {
  slug: string;
  title: string;
  description: string;
  href: string;
  iconBackground: string;
  iconForeground: string;
};

/** A curated "take it further" tool, resolved on the server. */
export type TemplateRecommendation = {
  slug: string;
  title: string;
  /** Namespace that translates `title`. */
  titleNs: 'recommendations' | 'tools';
  message: string;
  href: string;
  image?: string;
  video?: string;
  iconBackground?: string;
  iconForeground?: string;
};

/**
 * Quick-start values hidden in a placeholder such as
 * "example: Fantasy, Sci-fi, Modern". Only short comma lists qualify; a
 * sentence-long example is better left as a placeholder.
 */
export function placeholderSuggestions(placeholder?: string): string[] {
  const match = placeholder?.match(/^\s*(?:example|e\.g\.)\s*:\s*(.+)$/i);
  if (!match) return [];
  const items = match[1]
    .split(',')
    .map((item) => item.trim().replace(/[.…]+$/, ''))
    .filter(Boolean);
  if (items.length < 2 || items.length > 5) return [];
  if (items.some((item) => item.length > 24)) return [];
  return items;
}

/** Options short enough to show as chips instead of a dropdown. */
export function fitsAsChips(options: readonly string[] = []) {
  return (
    options.length > 0 &&
    options.length <= 6 &&
    options.every((option) => option.length <= 22)
  );
}

/** A field's options as plain values (selects and radio groups). */
export function fieldOptions(field: InputField): string[] {
  if (field.type === 'radio') {
    return (field.radioOptions || []).map((option) => option.name);
  }
  return [...(field.options || [])];
}
